import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { MarketingFooter } from "@/features/marketing/MarketingFooter";
import { publicWebCopyForLang } from "@/shared/i18n/public-web-copy";
import { useMarketingExperience } from "@/shared/i18n/use-product-locale";
import { useAuth } from "../auth/AuthProvider";
import { BrandMark } from "@/shared/brand/BrandMark";
import "./MarketingLayout.css";

export function MarketingLayout() {
  const { signedIn } = useAuth();
  const location = useLocation();
  const entrySurface = location.pathname === "/onboarding" || location.pathname.startsWith("/auth/");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const { lang: marketingLang } = useMarketingExperience({
    preferFirstRun: location.pathname === "/onboarding",
  });
  const layoutLang = marketingLang;
  const copy = useMemo(() => publicWebCopyForLang(layoutLang), [layoutLang]);

  useEffect(() => {
    if (!mobileNavOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setMobileNavOpen(false);
      menuButtonRef.current?.focus();
    }

    function handlePointerDown(event: PointerEvent) {
      if (headerRef.current?.contains(event.target as Node)) return;
      setMobileNavOpen(false);
    }

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [mobileNavOpen]);

  function closeMobileNav() {
    setMobileNavOpen(false);
  }

  return (
    <div className="mkt-layout">
      <a className="skip-link" href="#main-content">
        {copy("marketing.layout.skip")}
      </a>
      {entrySurface ? <header className="mkt-entry-header">
        <Link to="/" className="mkt-logo"><BrandMark />Today&apos;s Brief</Link>
        <Link to="/app/brief">{copy("home.brief")} <span className="tb-directional-glyph" aria-hidden="true">→</span></Link>
      </header> : <header className="mkt-header" ref={headerRef}>
        <Link to="/" className="mkt-logo" onClick={closeMobileNav}>
          <BrandMark className="mkt-brand-mark" />
          Today&apos;s Brief
        </Link>
        <button
          ref={menuButtonRef}
          type="button"
          className="mkt-menu-toggle"
          aria-label={copy("marketing.layout.nav")}
          aria-controls="marketing-navigation"
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          <span className="mkt-menu-toggle__icon" aria-hidden="true">
            <span />
            <span />
          </span>
        </button>
        <nav
          id="marketing-navigation"
          className="mkt-nav"
          aria-label={copy("marketing.layout.nav")}
          data-open={mobileNavOpen}
        >
          <Link to="/#product-tour" onClick={closeMobileNav}>
            {copy("marketing.footer.product")}
          </Link>
          <Link to="/#platforms" onClick={closeMobileNav}>
            iOS
          </Link>
          <Link to="/pricing" onClick={closeMobileNav}>
            {copy("marketing.layout.pricing")}
          </Link>
          <Link to="/faq" onClick={closeMobileNav}>
            {copy("marketing.layout.faq")}
          </Link>
          {signedIn ? (
            <Link to="/app/brief" className="tb-btn tb-btn--primary" onClick={closeMobileNav}>
              {copy("marketing.layout.openBrief")}
            </Link>
          ) : (
            <>
              <Link to="/auth/sign-in" onClick={closeMobileNav}>
                {copy("marketing.layout.signIn")}
              </Link>
              <Link to="/onboarding" className="tb-btn tb-btn--primary" onClick={closeMobileNav}>
                {copy("marketing.layout.getStarted")}
              </Link>
            </>
          )}
        </nav>
      </header>}
      <main id="main-content" className="mkt-main" tabIndex={-1} data-route-focus-target>
        <Outlet />
      </main>
      {!entrySurface ? <MarketingFooter lang={layoutLang} /> : null}
    </div>
  );
}
